import { uuidv4 } from "@-/com.npmjs.uuid";
import * as z from "@-/com.npmjs.zod";
import type { MapDownloadStore } from "#map-download-storage-types";

const CACHE_NAME = "haniverse-offline-maps-v1";
const entrySchema = z.object({
  bytes: z.number().positive(),
  downloadedAt: z.number().positive(),
  url: z.string().url(),
});
const indexKey = (key: string) => `offline-map-v1:${key}`;
function readEntry(key: string) {
  const json = localStorage.getItem(indexKey(key));
  if (json === null) {
    return null;
  }
  try {
    return entrySchema.parse(JSON.parse(json));
  } catch {
    return null;
  }
}
async function getCache() {
  return typeof caches === "undefined" ? null : caches.open(CACHE_NAME);
}

/** Browser Cache Storage holds only explicit downloads, independently of the HTTP cache. */
export const mapDownloadStore: MapDownloadStore = {
  async info(key) {
    const entry = readEntry(key);
    if (entry === null) {
      return null;
    }
    const cache = await getCache();
    const response = await cache?.match(entry.url);
    if (response === undefined) {
      return null;
    }
    return { bytes: entry.bytes, downloadedAt: entry.downloadedAt };
  },
  async read(key) {
    const entry = readEntry(key);
    if (entry === null) {
      return null;
    }
    const cache = await getCache();
    const response = await cache?.match(entry.url);
    return response === undefined ? null : response.text();
  },
  async remove(key) {
    const entry = readEntry(key);
    localStorage.removeItem(indexKey(key));
    if (entry !== null) {
      const cache = await getCache();
      await cache?.delete(entry.url);
    }
  },
  async write(key, json, info, signal) {
    const cache = await getCache();
    if (cache === null) {
      throw new Error("Offline map storage is unavailable in this browser.");
    }
    signal.throwIfAborted();
    const previous = readEntry(key);
    const url = `${key}?download=${uuidv4()}`;
    try {
      await cache.put(url, new Response(json, { headers: { "Content-Type": "application/json" } }));
      signal.throwIfAborted();
      // Cache writes cannot be aborted. Publish a synchronous pointer only after completion,
      // so a late/cancelled write never replaces a usable download.
      localStorage.setItem(indexKey(key), JSON.stringify({ ...info, url }));
    } catch (error) {
      await cache.delete(url);
      throw error;
    }
    if (previous !== null) {
      await cache.delete(previous.url).catch(() => {});
    }
  },
};
